import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_NUMERIC_TEXT_INPUT,
  OPTIONAL_QUANTITY_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "./optionalInputs";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Cost Agreement
 *
 * Takes ONE supplier cost-agreement LINE as single-value input sockets and
 * writes `ap_supplier_cost_agreements` plus its quantity-break tiers, shaped
 * after the Gently model rather than after any source file. Downstream of the
 * "Observe DataSource Link" trigger: the trigger's lockstep fanout zips its
 * per-column arrays into one item per row, and this step's `batch` config runs
 * the upserts in chunks. A leaf step — per-row outputs are execution results,
 * not a downstream data contract.
 *
 * See `docs/procurement/supplier-cost-agreements.md` for the model, the
 * overlap-resolution rules the engine applies on read, and why this data cannot
 * live in `ap_supplier_product_price_breaks`.
 *
 * IDENTITY: `origin_id`, NOT the SKU
 * ---------------------------------
 * One row is one contract line, keyed `(organization, origin_system, origin_id)`
 * where `origin_id` is `<division>:<contract number>:<contract line>`. Keying on
 * the origin is what lets two contracts cover the same SKU and location in
 * overlapping windows — which is not a theoretical case: one division measured
 * 2,612 SKU-location keys with two concurrently-active contracts. A nightly run
 * therefore RECONCILES contract lines; it never stamps "today's cost" onto a SKU.
 *
 * THE DIVISION COMES FROM THE LINK, NOT FROM A COLUMN
 * --------------------------------------------------
 * Contract numbers are assigned per source instance, so identity is only unique
 * once scoped by division — and the per-division source tables carry no division
 * column at all. The division is therefore read from the wired Data Source
 * Link's DataSource title, upper-cased, exactly as `upsert_vendor_rexel` derives
 * it. A row whose link has no resolvable title is skipped and reported rather
 * than written under a guessed division: a wrong division silently merges two
 * divisions' contract 4046 into one row.
 *
 * VALID TIME IS REQUIRED
 * ----------------------
 * `effectiveStart` / `effectiveEnd` are the point of this table — they answer
 * "what does this cost if the buyer places the order on Monday", independently of
 * when the row was ingested. Both are NOT NULL, so a row whose window will not
 * parse is skipped and reported (`invalidWindow`) rather than defaulted to the
 * run date, which would silently assert a window the vendor never agreed to.
 * Both sockets accept an ISO `YYYY-MM-DD` or a packed numeric `YYYYMMDD`, since
 * the graph has no arithmetic with which to reformat a source column.
 *
 * SCOPE IS DERIVED, NOT WIRED
 * ---------------------------
 * The source expresses location two mutually-exclusive ways: a single opaque
 * branch character (`locationCode`) or a district code covering a group of
 * branches (`districtCode`). `scope_kind` is derived from which one is present —
 * `warehouse`, `district`, or `division` when neither is — and the verbatim code
 * is kept as `scope_code`. That keeps "this contract is division-wide" (correct
 * data) distinguishable from "the source named a branch we could not resolve" (a
 * data-quality bug), which a nullable `warehouseId` alone would conflate. The
 * engine excludes unresolved warehouse scopes from costing for that reason.
 *
 * Resolving that branch character to a warehouse is a SEPARATE question from
 * deriving the scope. `warehouseCode` is the optional answer to it: a source
 * whose branch code already is a warehouse origin id needs nothing wired, and a
 * source whose branch code is a private one-character key needs an upstream
 * lookup to translate it. Either way `scope_kind` and `scope_code` are unchanged
 * — the decode decides only which warehouse the scope points at.
 *
 * TIERS ARE A COMPLETE SNAPSHOT
 * -----------------------------
 * The three tier pairs are fixed columns of the source record, so a tier left
 * empty is a statement that the contract does not offer it, not an absence of
 * information. Any previously persisted tier not present in this run is DELETED,
 * including one supplied half-specified: the source touched it and gave us
 * something unusable, and a stale discount that makes the engine UNDER-cost is
 * worse than losing a tier. If a source ever needs partial-update semantics it
 * must not use these sockets.
 */
export const upsertCostAgreementStep = defineStepConfig({
  type: "upsert_cost_agreement",
  name: "Upsert Cost Agreement",
  description:
    "Upserts one supplier cost-agreement line (contract number/line, SKU, scope, valid-time window, quantity-break tiers) into ap_supplier_cost_agreements and its tier table. Keyed on <division>:<contract>:<line> so overlapping contracts on the same SKU coexist. The division comes from the wired Data Source Link. Fan a trigger's per-field array outputs into its single-value inputs.",
  category: "Transformation",
  libraryModule: "Procurement",

  experimental: {
    reason:
      "First ingest path for supplier cost agreements. The engine does not read these rows yet — phase 2 cost resolution, buy-forward sizing and the suggestion provenance columns are still to be built (docs/procurement/supplier-cost-agreements.md).",
  },

  inputSchema: z.object({
    // ── Ingest provenance, and the division ─────────────────────────────────
    // The link is broadcast across the batch by the trigger's fanout. Its
    // DataSource title is the division code, so this socket is REQUIRED: no
    // division means no row identity.
    dataSourceLink: z.object({
      id: z.string().uuid(),
      dataSourceId: z.string().uuid(),
      schemaName: z.string(),
      tableName: z.string(),
    }),

    // ── Identity ────────────────────────────────────────────────────────────
    contractNumber: QUANTITY_INPUT.describe(
      "Contract number as the source assigns it. Scoped by division, not globally unique.",
    ),
    contractLine: QUANTITY_INPUT.describe(
      "Line number within the contract. One row is one LINE.",
    ),
    supplierCode: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
      "Vendor code, resolved to an ap_suppliers.origin_id. Accepts a number. See supplierCodeStrategy for division-prefixed supplier catalogs.",
    ),
    productSku: z
      .string()
      .describe(
        "Product SKU, resolved to an im_products_v3.key — the engine's product identity.",
      ),

    // ── Valid time ──────────────────────────────────────────────────────────
    effectiveStart: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
      "First date the vendor honors this cost. ISO YYYY-MM-DD or packed YYYYMMDD. Required — a row without a window is skipped, never defaulted.",
    ),
    effectiveEnd: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
      "Last date the vendor honors this cost. ISO YYYY-MM-DD or packed YYYYMMDD. Required — a row without a window is skipped, never defaulted.",
    ),

    // ── Scope ───────────────────────────────────────────────────────────────
    locationCode: OPTIONAL_TEXT_INPUT.describe(
      "Source branch code. Present makes the row warehouse-scoped; it is resolved to a warehouse where possible and kept verbatim as scope_code either way.",
    ),
    districtCode: OPTIONAL_TEXT_INPUT.describe(
      "Source district code covering a group of branches. Present (with no location) makes the row district-scoped. Wired together with locationCode, the location wins — the source sets them exclusively.",
    ),
    // Optional, and NOT a replacement for locationCode. A source whose branch
    // code already IS the warehouse's origin id resolves with this unwired; a
    // source whose branch code is a private key resolves only once a lookup
    // has translated it. `scope_code` is the verbatim source code either way,
    // so `scope_kind`/`scope_code` keep meaning exactly what they meant before.
    warehouseCode: OPTIONAL_TEXT_INPUT.describe(
      "The warehouse origin id that locationCode decodes to, when a lookup upstream supplied it. Tried before locationCode when resolving the warehouse; never replaces it as scope_code.",
    ),

    // ── Terms ───────────────────────────────────────────────────────────────
    minQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Contract minimum order quantity. Absent is not the same as 0, and 0 (meaning 'none specified') is real source data.",
    ),
    incrementalQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Order increment above the minimum. Absent is not the same as 0.",
    ),
    contractDescription: OPTIONAL_TEXT_INPUT.describe(
      "Buyer-facing contract description, verbatim from the source.",
    ),
    sourceStatus: OPTIONAL_TEXT_INPUT.describe(
      "Contract status code, verbatim. Not interpreted here — an unknown status must not decide whether a contract loads.",
    ),
    sourceCreatedOn: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
      "Date the contract line was created in the source. ISO YYYY-MM-DD or packed YYYYMMDD.",
    ),
    vendorContract: OPTIONAL_TEXT_INPUT.describe(
      "The vendor's own contract reference, recorded in metadata for buyer lookups.",
    ),

    // ── Quantity-break tiers (a complete snapshot) ──────────────────────────
    tier1MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 1: minimum quantity",
    ),
    tier1UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 1: unit cost at that quantity",
    ),
    tier2MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 2: minimum quantity",
    ),
    tier2UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 2: unit cost at that quantity",
    ),
    tier3MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 3: minimum quantity",
    ),
    tier3UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Tier 3: unit cost at that quantity",
    ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The contract line was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped; one of the flags below says why"),
    unresolvedDivision: z
      .boolean()
      .describe(
        "The wired Data Source Link's DataSource has no usable title, so the row has no division and no identity",
      ),
    unresolvedSupplier: z
      .boolean()
      .describe("No supplier matched the vendor code"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    invalidWindow: z
      .boolean()
      .describe(
        "The valid-time window is missing, unparseable, or ends before it starts",
      ),
    invalidScope: z
      .boolean()
      .describe(
        "The source's location or district code is too long for scope_code, so the scope cannot be recorded",
      ),
    filteredExpired: z
      .boolean()
      .describe(
        "The contract expired longer ago than retainExpiredDays, so it was deliberately not loaded",
      ),
    scopeKind: z
      .string()
      .nullable()
      .describe("Derived scope: 'warehouse', 'district' or 'division'"),
    warehouseResolved: z
      .boolean()
      .describe(
        "A warehouse-scoped row whose branch code resolved to a warehouse. False on a warehouse-scoped row means the scope is unresolved and the engine will not cost against it.",
      ),
    tiersWritten: z
      .number()
      .describe("How many quantity-break tiers were persisted"),
    incompleteTiers: z
      .array(z.number())
      .describe(
        "Tiers given a quantity without a cost, or a cost without a quantity. Dropped rather than written as a zero.",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
    /**
     * Separate from `originSystem` for the same reason as on
     * `upsert_supplier_product`: the warehouse seed and the vendor ingest stamp
     * different values, so one filter cannot pin both, and the failure mode is
     * silent — every row skipped with the step still reporting success.
     */
    supplierOriginSystem: z
      .string()
      .optional()
      .describe(
        "Restrict SUPPLIER resolution to references ingested from this origin system. Leave empty to match any, in which case a code present in several sources resolves to nothing and is reported.",
      ),
    writeOriginSystem: z
      .string()
      .prefault("as400")
      .describe(
        "Value written to the row's origin_system column, and part of its upsert key. Independent of the resolution filters above: one narrows what we READ, this labels what we WRITE.",
      ),
    /**
     * Vendor numbers are assigned per division, so an unprefixed code cannot
     * identify a supplier across 15 divisions — which is why the vendor ingest
     * keys `ap_suppliers.origin_id` on the division-prefixed, zero-padded code
     * (vendor 43 on division QE becomes `QE00043`). A graph cannot compose that
     * key: socket expressions have no arithmetic and the division is not a
     * column. So the composition is a named strategy here rather than upstream
     * wiring.
     */
    supplierCodeStrategy: z
      .enum(["as_is", "division_prefixed"])
      .prefault("as_is")
      .describe(
        "How to turn the vendor code into a supplier origin id. 'as_is' uses it verbatim. 'division_prefixed' looks up <DIVISION><code zero-padded to 5>, the key the AS/400 vendor ingest writes, and falls back to the verbatim code.",
      ),
    /**
     * Most of the source is history: 366,764 of one division's 405,865 rows are
     * already expired. Loading all of it would bloat the engine's read path for
     * nothing, and loading none of it makes "why did we pay that" unanswerable.
     */
    retainExpiredDays: z
      .number()
      .int()
      .nonnegative()
      .prefault(90)
      .describe(
        "Load contracts whose effective_end is within this many days of the run date; drop older ones and report them as filteredExpired. 0 keeps only contracts that have not yet ended.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
